import { DataSet } from 'vis/index-timeline-graph2d';
import {
  EDGE_TYPES,
  VALUE_PROPOSITION_NODE_ID,
  NETWORK_LAYERS,
  NETWORK_LAYERS_ARRAY
} from './constants';

const REGULAR_EDGE_WIDTH = 1;
const REGULAR_EDGE_LENGTH = 35;
const MAIN_EDGE_WIDTH = 5;
const MAIN_EDGE_LENGTH = 300;
const LARGE_EDGE_LENGTH = 180;

const REGULAR_EDGE_COLOR = '#424242';
const EDGE_TO_CURRENT_MVP_COLOR = '#0fa69a';

export default class NetworkEdges {
  constructor(network) {
    this.network = network;
    this._edges = {};
    NETWORK_LAYERS_ARRAY.forEach(
      layer => (this._edges[layer] = new DataSet([]))
    );
  }

  get edges() {
    return this._edges[this.currentLayer];
  }

  get currentLayer() {
    return this.network.currentLayer;
  }

  addMultiple(edges) {
    const allowedEdges = {};
    NETWORK_LAYERS_ARRAY.forEach(layer => (allowedEdges[layer] = []));
    edges.forEach(edge => {
      if (NETWORK_LAYERS_ARRAY.includes(edge.layer)) {
        allowedEdges[edge.layer].push(edge);
      }
    });
    NETWORK_LAYERS_ARRAY.forEach(layer => {
      try {
        if (allowedEdges[layer].length > 0) {
          this._edges[layer].add(allowedEdges[layer]);
        }
      } catch (err) {
        console.log(err);
      }
    });
  }

  drawPathToCurrentMvp() {
    const { mvps } = this.network;
    const { currentMvpId } = this.network;
    const edgesIds = {};
    for (const layer of NETWORK_LAYERS_ARRAY) {
      edgesIds[layer] = [];
    }
    // Color all edges equally
    this._reinitEdgesColor();
    this._calcEdgesToCurrentMvp(edgesIds, mvps, currentMvpId);
    this._colorEdgesToCurrentMpv(edgesIds);
  }

  updateCurrentMvpEdges(oldEdgesIds, newEdges) {
    const layer = NETWORK_LAYERS.PRODUCT;
    for (const edgeId of oldEdgesIds) {
      this._edges[layer].remove(edgeId);
    }
    // Add new edges
    this._edges[layer].update(newEdges);
  }

  removeCurrentMvpEdges() {
    NETWORK_LAYERS_ARRAY.forEach(layer => {
      const edgesIds = this._edges[layer]
        .get({
          filter: item => item.type == EDGE_TYPES.CURRENT_MVP
        })
        .map(el => el.id);
      this.remove(edgesIds, layer);
    });
  }

  removeFeatureNodeEdges(id, layer = NETWORK_LAYERS.PRODUCT) {
    const targetType =
      layer == NETWORK_LAYERS.PRODUCT
        ? EDGE_TYPES.MVP_FEATURE
        : EDGE_TYPES.SECTION_FEATURE;
    const edgesIds = this._edges[layer]
      .get({
        filter: item => item.type == targetType && item.from == id
      })
      .map(el => el.id);
    this.remove(edgesIds, layer);
  }

  removeLinkingNodeEdges(id, layer = NETWORK_LAYERS.PRODUCT) {
    const edgesIds = this._edges[layer]
      .get({
        filter: item =>
          item.type == EDGE_TYPES.MVP_STARTUP_SECTION && item.to == id
      })
      .map(el => el.id);
    this.remove(edgesIds, layer);
  }

  update(oldEdgesIds, newEdges, layer) {
    layer = layer == undefined ? this.currentLayer : layer;
    const newEdgesIds = newEdges.map(edge => edge.id);
    // Remove old edges if they do not belong to newEdges
    for (const edgeId of oldEdgesIds) {
      if (!newEdgesIds.includes(edgeId)) {
        this._edges[layer].remove(edgeId);
      }
    }
    // Add new edges
    this._edges[layer].update(newEdges);

    this.drawPathToCurrentMvp();
  }

  _calcEdgesToCurrentMvp(edgesIds, mvps, initialMvpId) {
    const mvp = mvps.find(el => el.id == initialMvpId);
    if (mvp) {
      for (const id of mvp.priorMvpIds) {
        const mvpNodeId = mvp.nodeMappingId;
        const priorMvp = mvps.find(el => el.id == id);
        // priorMvp could already have been destroyed
        if (priorMvp) {
          const priorMvpNodeId = priorMvp.nodeMappingId;
          edgesIds[NETWORK_LAYERS.PRODUCT].push(
            NetworkEdges.edgeId(EDGE_TYPES.MVP, mvpNodeId, priorMvpNodeId)
          );
          this._calcEdgesForSectionsLayers(edgesIds, mvp);
          // UNCOMMENT TO REACTIVATE THE BMC LAYER
          // this._calcEdgesForTheBmcLayer(edgesIds, mvp);
          try {
            this._calcEdgesToCurrentMvp(edgesIds, mvps, id);
          } catch (err) {
            console.log('Caught an error while calculating edges', err);
          }
        }
      }
      this._calcEdgesToValueProposition(edgesIds, mvp);
    }
  }

  /* UNCOMMENT TO REACTIVATE THE BMC LAYER
  _calcEdgesForTheBmcLayer(edgesIds,mvp){
      const bmc = mvp.virtualBmcSection;
      const bmcEdgesIds = bmc.edgesFromParent.map((edge) => edge.id);
      edgesIds[NETWORK_LAYERS.BMC] = [...edgesIds[NETWORK_LAYERS.BMC], ...bmcEdgesIds];
  }
  */

  _calcEdgesForSectionsLayers(edgesIds, mvp) {
    const { sections } = mvp;
    for (const section of sections) {
      const newEdgesIds = section.edgesFromPriorSections.map(edge => edge.id);
      edgesIds[section.layer] = [...edgesIds[section.layer], ...newEdgesIds];
    }
  }

  _calcEdgesToValuePropositionFromSections(edgesIds, mvp) {
    if (mvp.priorMvpIds.length == 0) {
      const { sections } = mvp;
      for (const section of sections) {
        const vpEdge = section.edgesFromValueProposition[0];
        edgesIds[section.layer].push(vpEdge.id);
      }
    }
  }

  _calcEdgesToValueProposition(edgesIds, mvp) {
    if (mvp.priorMvpIds.length == 0) {
      const mvpNodeId = mvp.nodeMappingId;
      edgesIds[NETWORK_LAYERS.PRODUCT].push(
        NetworkEdges.edgeId(EDGE_TYPES.VALUE_PROPOSITION, mvpNodeId)
      );
      this._calcEdgesToValuePropositionFromSections(edgesIds, mvp);
      // UNCOMMENT TO REACTIVATE THE BMC LAYER
      // this._calcEdgesForTheBmcLayer(edgesIds, mvp);
    }
  }

  _colorEdgesToCurrentMpv(edgesIds) {
    const edges = [];
    for (const layer of NETWORK_LAYERS_ARRAY) {
      for (const id of edgesIds[layer]) {
        edges.push({
          id,
          color: {
            color: EDGE_TO_CURRENT_MVP_COLOR,
            highlight: EDGE_TO_CURRENT_MVP_COLOR
          }
        });
      }
      this._edges[layer].update(edges);
    }
  }

  _reinitEdgesColor() {
    let edges;
    for (const layer of NETWORK_LAYERS_ARRAY) {
      edges = [];
      this._edges[layer].forEach(edge => {
        if (
          edge.color &&
          (edge.color.color != REGULAR_EDGE_COLOR ||
            edge.color.highlight != REGULAR_EDGE_COLOR)
        ) {
          edges.push({
            id: edge.id,
            color: { color: REGULAR_EDGE_COLOR, highlight: REGULAR_EDGE_COLOR }
          });
        }
      });
      this._edges[layer].update(edges);
    }
  }

  removeEdgesTouching(nodeId, layer) {
    layer = layer == undefined ? this.currentLayer : layer;
    const edgesIds = this._edges[layer]
      .get({
        filter: item => item.to == nodeId || item.from == nodeId
      })
      .map(el => el.id);
    this.remove(edgesIds);
  }

  remove(ids, layer) {
    layer = layer || this.currentLayer;
    this._edges[layer].remove(ids);
  }

  removeFromTo(fromId, toId, layer) {
    layer = layer || this.currentLayer;
    const edgesIds = this._edges[layer]
      .get({
        filter: item => item.to == toId && item.from == fromId
      })
      .map(el => el.id);
    this.remove(edgesIds, layer);
  }

  clearAll() {
    this.remove(this._edges[this.currentLayer].getIds());
  }
}

NetworkEdges.edgeId = (type, to, from = VALUE_PROPOSITION_NODE_ID) =>
  `${type}-${to}-${from}`;

NetworkEdges.edgeWidth = type => {
  switch (type) {
    case EDGE_TYPES.VALUE_PROPOSITION:
    case EDGE_TYPES.MVP:
    case EDGE_TYPES.STARTUP_SECTION:
    case EDGE_TYPES.BMC_MAIN:
      return MAIN_EDGE_WIDTH;
    default:
      return REGULAR_EDGE_WIDTH;
  }
};

NetworkEdges.edgeLength = type => {
  switch (type) {
    case EDGE_TYPES.VALUE_PROPOSITION:
    case EDGE_TYPES.MVP:
    case EDGE_TYPES.STARTUP_SECTION:
    case EDGE_TYPES.BMC_MAIN:
      return MAIN_EDGE_LENGTH;
    case EDGE_TYPES.CURRENT_MVP:
      return LARGE_EDGE_LENGTH;
    default:
      return REGULAR_EDGE_LENGTH;
  }
};

NetworkEdges.arrows = type => {
  switch (type) {
    case EDGE_TYPES.VALUE_PROPOSITION:
    case EDGE_TYPES.MVP:
    case EDGE_TYPES.STARTUP_SECTION:
    case EDGE_TYPES.BMC_MAIN:
      return true;
    default:
      return false;
  }
};

NetworkEdges.generateEdge = (
  type,
  to,
  from = VALUE_PROPOSITION_NODE_ID,
  layer = NETWORK_LAYERS.PRODUCT
) => {
  const id = NetworkEdges.edgeId(type, to, from);
  return {
    id,
    from,
    to,
    width: NetworkEdges.edgeWidth(type),
    length: NetworkEdges.edgeLength(type),
    layer,
    type,
    color: {
      color: REGULAR_EDGE_COLOR,
      highlight: REGULAR_EDGE_COLOR
    },
    arrows: {
      to: NetworkEdges.arrows(type)
    },
    arrowStrikethrough: false
  };
};
